import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, TrendingDown, Award } from 'lucide-react';
import { services, testimonials } from '../data/mockData';

const Home = () => {
  return (
    <div className="page">
      {/* Hero Section - Welcoming & Educational */}
      <section className="hero-section hero-with-image">
        <div className="hero-background-image">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw1fHxvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3MzYzNDIwMDB8MA&ixlib=rb-4.1.0&q=85"
            alt="Modern sustainable office building"
            loading="eager"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title" style={{fontSize: '52px', lineHeight: '1.15', marginBottom: '20px'}}>
            Smarter Energy Management
            <br />
            <span style={{color: '#ffa726'}}>for Modern Buildings</span>
          </h1>
          <p className="hero-subtitle" style={{fontSize: '22px', maxWidth: '800px', margin: '0 auto 30px', lineHeight: '1.6'}}>
            We help hotels, commercial properties, and industrial facilities across the UK, UAE, and Sweden optimize their energy systems—without disrupting operations or compromising comfort.
          </p>
          <div className="hero-buttons">
            <Link to="/about" className="btn-secondary" style={{fontSize: '18px', padding: '16px 32px'}}>
              Learn About Our Approach
            </Link>
            <a href="#our-solutions" className="btn-secondary" style={{fontSize: '18px', padding: '16px 32px', background: 'transparent', border: '2px solid white', color: 'white'}}>
              Explore Solutions
            </a>
          </div>
          <div style={{marginTop: '30px', fontSize: '14px', opacity: '0.9'}}>
            <strong>Trusted by 500+ facilities</strong> across three continents
          </div>
        </div>
      </section>

      {/* Value Proposition - Problem & Approach */}
      <section className="section" style={{padding: '80px 0', background: 'white'}}>
        <div className="container">
          <div style={{maxWidth: '900px', margin: '0 auto', textAlign: 'center'}}>
            <h2 className="heading-2" style={{fontSize: '38px', marginBottom: '25px'}}>
              Energy Costs Keep Rising. Your Operations Shouldn't Suffer.
            </h2>
            <p style={{fontSize: '19px', lineHeight: '1.8', color: '#555', marginBottom: '40px'}}>
              Traditional energy efficiency solutions often require major capital investment, operational disruption, or behavioral changes. 
              We believe there's a better way—technology that works <em>with</em> your existing systems, installs without downtime, 
              and delivers measurable results from day one.
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', maxWidth: '1100px', margin: '50px auto 0'}}>
            <div style={{textAlign: 'center', padding: '30px'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>🔧</div>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Non-Invasive</h3>
              <p style={{fontSize: '15px', color: '#666', lineHeight: '1.6'}}>
                Our solutions integrate with your existing HVAC, heating, and cooling systems—no replacement, no overhaul.
              </p>
            </div>

            <div style={{textAlign: 'center', padding: '30px'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>📊</div>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Data-Driven</h3>
              <p style={{fontSize: '15px', color: '#666', lineHeight: '1.6'}}>
                We measure everything. Real installations, verified data, transparent reporting. No exaggerated claims.
              </p>
            </div>

            <div style={{textAlign: 'center', padding: '30px'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>⚡</div>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Zero Downtime</h3>
              <p style={{fontSize: '15px', color: '#666', lineHeight: '1.6'}}>
                Installations happen during normal operations. Your guests, customers, and staff won't notice a thing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Solutions - Soft Introduction */}
      <section id="our-solutions" className="section" style={{padding: '80px 0', background: '#f9f9f9'}}>
        <div className="container">
          <div className="section-header" style={{textAlign: 'center', marginBottom: '60px'}}>
            <h2 className="heading-2" style={{fontSize: '38px', marginBottom: '20px'}}>
              Technologies We Deploy
            </h2>
            <p className="section-subtitle" style={{fontSize: '18px', maxWidth: '800px', margin: '0 auto', color: '#666'}}>
              From frequency programming to IoT environmental monitoring, we bring proven technologies 
              that reduce energy costs while maintaining—or improving—comfort and operational efficiency.
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto'}}>
            <div style={{background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '15px', color: '#00897b'}}>OH4 Heat Transfer System</h3>
              <p style={{fontSize: '15px', color: '#555', lineHeight: '1.7', marginBottom: '20px'}}>
                Frequency programming technology that optimizes water molecular structure in heating and cooling systems. 
                Non-invasive, no moving parts, 10+ year lifespan.
              </p>
              <Link to="/oh4" style={{color: '#00897b', fontSize: '15px', fontWeight: '600', textDecoration: 'none'}}>
                Learn More →
              </Link>
            </div>

            <div style={{background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '15px', color: '#00897b'}}>FresherMat</h3>
              <p style={{fontSize: '15px', color: '#555', lineHeight: '1.7', marginBottom: '20px'}}>
                Magnetic preservation strips that extend produce freshness by 3-5 days. Perfect for hotels, restaurants, 
                and commercial kitchens reducing food waste.
              </p>
              <Link to="/freshermat" style={{color: '#00897b', fontSize: '15px', fontWeight: '600', textDecoration: 'none'}}>
                Learn More →
              </Link>
            </div>

            <div style={{background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '15px', color: '#00897b'}}>IoT Environmental Sensors</h3>
              <p style={{fontSize: '15px', color: '#555', lineHeight: '1.7', marginBottom: '20px'}}>
                Real-time monitoring of temperature, humidity, air quality, and more. Data-driven insights 
                for facilities management and ESG reporting.
              </p>
              <Link to="/iot-sensors" style={{color: '#00897b', fontSize: '15px', fontWeight: '600', textDecoration: 'none'}}>
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Point Introduction */}
      <section className="section" style={{padding: '60px 0', background: 'white', textAlign: 'center'}}>
        <div className="container">
          <div style={{maxWidth: '800px', margin: '0 auto'}}>
            <p style={{fontSize: '16px', color: '#666', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600'}}>
              Real Installations, Real Data
            </p>
            <h2 style={{fontSize: '32px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a'}}>
              We Believe in Transparent Reporting
            </h2>
            <p style={{fontSize: '17px', color: '#555', lineHeight: '1.7'}}>
              Below is verified data from an ongoing pilot project at Radisson Beach Resort Palm Jumeirah. 
              We share real performance data—including limitations—because trust matters more than marketing.
            </p>
          </div>
        </div>
      </section>

      {/* RADISSON PILOT PROJECT */}
      <section id="radisson-case-study" className="section" style={{background: 'linear-gradient(135deg, #004d40 0%, #00695c 100%)', color: 'white', padding: '60px 0'}}>
        <div className="container">
          <div className="section-header" style={{textAlign: 'center', marginBottom: '40px'}}>
            <h2 className="heading-2" style={{color: 'white', fontSize: '36px', marginBottom: '10px'}}>
              Pilot Project: Radisson Beach Resort Palm Jumeirah
            </h2>
            <p style={{fontSize: '18px', color: '#b2dfdb', maxWidth: '800px', margin: '0 auto'}}>
              14-month pilot project evaluating OH4 performance. December 2025 data shows 30.2% reduction - significant improvement from initial 18.1% (October 2024).
            </p>
          </div>

          {/* Data Comparison Table */}
          <div style={{background: 'white', borderRadius: '12px', padding: '30px', maxWidth: '900px', margin: '0 auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{borderBottom: '2px solid #004d40'}}>
                  <th style={{padding: '15px', textAlign: 'left', color: '#004d40', fontSize: '16px', fontWeight: '600'}}>Metric</th>
                  <th style={{padding: '15px', textAlign: 'right', color: '#004d40', fontSize: '16px', fontWeight: '600'}}>December 2024</th>
                  <th style={{padding: '15px', textAlign: 'right', color: '#004d40', fontSize: '16px', fontWeight: '600'}}>December 2025</th>
                  <th style={{padding: '15px', textAlign: 'right', color: '#00897b', fontSize: '16px', fontWeight: '700'}}>Savings</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{borderBottom: '1px solid #e0e0e0'}}>
                  <td style={{padding: '15px', color: '#333', fontSize: '15px'}}>Energy Consumed</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>14,971 kWh</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>10,454 kWh</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#00897b', fontSize: '16px', fontWeight: '700'}}>4,517 kWh</td>
                </tr>
                <tr style={{borderBottom: '1px solid #e0e0e0'}}>
                  <td style={{padding: '15px', color: '#333', fontSize: '15px'}}>Cost per kWh</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>0.38 AED</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>0.38 AED</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#00897b', fontSize: '16px', fontWeight: '700'}}>—</td>
                </tr>
                <tr style={{borderBottom: '1px solid #e0e0e0'}}>
                  <td style={{padding: '15px', color: '#333', fontSize: '15px'}}>Total Cost</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>6,647 AED</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#333', fontSize: '15px', fontWeight: '500'}}>4,642 AED</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#00897b', fontSize: '16px', fontWeight: '700'}}>2,006 AED</td>
                </tr>
                <tr style={{background: '#e8f5e9'}}>
                  <td style={{padding: '15px', color: '#1b5e20', fontSize: '16px', fontWeight: '600'}}>Reduction</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#1b5e20', fontSize: '15px'}}>—</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#1b5e20', fontSize: '15px'}}>—</td>
                  <td style={{padding: '15px', textAlign: 'right', color: '#1b5e20', fontSize: '20px', fontWeight: '700'}}>30.2%</td>
                </tr>
              </tbody>
            </table>
            
            <div style={{marginTop: '25px', padding: '20px', background: '#fff3e0', borderRadius: '8px', borderLeft: '4px solid #ff9800'}}>
              <p style={{margin: '0', color: '#e65100', fontSize: '13px', lineHeight: '1.6'}}>
                <strong>Pilot Project Update (December 2025):</strong> OH4 installed on one pool heat pump system (1F Pool Heat Pump PH-02720) in October 2024. 
                Data shows December 2024 vs December 2025 comparison. Performance improved from initial 18.1% (October) to 30.2% (December), suggesting 
                cumulative optimization effect. Results reflect one system over 14 months and should not be extrapolated as typical or guaranteed outcomes. 
                Engineering team at Radisson available for technical verification per pilot agreement.
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div style={{textAlign: 'center', marginTop: '35px'}}>
            <Link 
              to="/oh4/calculator.html" 
              style={{
                display: 'inline-block',
                padding: '16px 32px',
                background: '#ffa726',
                color: 'white',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '600',
                textDecoration: 'none',
                marginRight: '15px',
                boxShadow: '0 4px 12px rgba(255,167,38,0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Calculate Your Savings
            </Link>
            <a 
              href="/downloads/radisson-case-study.html" 
              target="_blank"
              style={{
                display: 'inline-block',
                padding: '16px 32px',
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              Download Full Case Study PDF
            </a>
          </div>
        </div>
      </section>

      {/* Pilot Project Context */}
      <section className="section" style={{padding: '40px 0', background: '#f5f5f5'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', maxWidth: '1000px', margin: '0 auto'}}>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>30.2%</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>December Result</div>
              <div style={{fontSize: '13px', color: '#888', marginTop: '5px'}}>Up from 18.1% (Oct)</div>
            </div>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>14</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>Months of Data</div>
              <div style={{fontSize: '13px', color: '#888', marginTop: '5px'}}>Ongoing monitoring</div>
            </div>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>Zero</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>Downtime</div>
              <div style={{fontSize: '13px', color: '#888', marginTop: '5px'}}>During installation</div>
            </div>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>1</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>System Tested</div>
              <div style={{fontSize: '13px', color: '#888', marginTop: '5px'}}>Single heat pump pilot</div>
            </div>
          </div>
        </div>
      </section>

      {/* OH4 SPOTLIGHT - Primary Solution with Infographic */}
      <section className="section" style={{padding: '80px 0', background: '#fafafa'}}>
        <div className="container">
          <div className="section-header" style={{textAlign: 'center', maxWidth: '900px', margin: '0 auto 60px'}}>
            <h2 className="heading-2" style={{fontSize: '42px', marginBottom: '15px', color: '#1a1a1a'}}>
              OH4 Heat Transfer System - Verified Results from Real Properties
            </h2>
            <p className="section-subtitle" style={{fontSize: '19px', color: '#555'}}>
              The proven technology behind Radisson's 30.2% energy savings (December 2025). Works with your existing HVAC, chillers, and pool systems.
            </p>
          </div>

          {/* How OH4 Works - Infographic Style */}
          <div style={{maxWidth: '1200px', margin: '0 auto 50px', background: 'white', borderRadius: '16px', padding: '50px 40px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)'}}>
            <h3 style={{fontSize: '32px', fontWeight: '700', textAlign: 'center', marginBottom: '45px', color: '#004d40'}}>
              How OH4 Works: Simple Physics, Extraordinary Results
            </h3>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '35px', marginBottom: '40px'}}>
              {/* Step 1 */}
              <div style={{textAlign: 'center', position: 'relative'}}>
                <div style={{
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px',
                  boxShadow: '0 4px 12px rgba(0,137,123,0.3)'
                }}>
                  <span style={{fontSize: '32px', fontWeight: '700', color: 'white'}}>1</span>
                </div>
                <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Frequency Programming</h4>
                <p style={{fontSize: '14px', color: '#666', lineHeight: '1.6'}}>
                  Specialized tape applied directly to heating pipes programs molecular water structure using specific frequencies
                </p>
              </div>

              {/* Step 2 */}
              <div style={{textAlign: 'center', position: 'relative'}}>
                <div style={{
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px',
                  boxShadow: '0 4px 12px rgba(0,137,123,0.3)'
                }}>
                  <span style={{fontSize: '32px', fontWeight: '700', color: 'white'}}>2</span>
                </div>
                <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Enhanced Heat Transfer</h4>
                <p style={{fontSize: '14px', color: '#666', lineHeight: '1.6'}}>
                  Modified molecular structure increases water's thermal conductivity by 15-35%, improving heat absorption and distribution
                </p>
              </div>

              {/* Step 3 */}
              <div style={{textAlign: 'center', position: 'relative'}}>
                <div style={{
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px',
                  boxShadow: '0 4px 12px rgba(0,137,123,0.3)'
                }}>
                  <span style={{fontSize: '32px', fontWeight: '700', color: 'white'}}>3</span>
                </div>
                <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Reduced Energy Demand</h4>
                <p style={{fontSize: '14px', color: '#666', lineHeight: '1.6'}}>
                  Your existing equipment (pumps, chillers, boilers) works less to achieve the same temperature - directly cutting energy consumption
                </p>
              </div>

              {/* Step 4 */}
              <div style={{textAlign: 'center', position: 'relative'}}>
                <div style={{
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px',
                  boxShadow: '0 4px 12px rgba(255,167,38,0.3)'
                }}>
                  <span style={{fontSize: '32px', fontWeight: '700', color: 'white'}}>✓</span>
                </div>
                <h4 style={{fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a'}}>Immediate Savings</h4>
                <p style={{fontSize: '14px', color: '#666', lineHeight: '1.6'}}>
                  Cost reductions appear immediately on your energy bills - no behavioral changes, no operational adjustments needed
                </p>
              </div>
            </div>

            {/* Key Stats Row */}
            <div style={{background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', borderRadius: '12px', padding: '35px', marginTop: '40px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '25px'}}>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '44px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>Up to 30%</div>
                  <div style={{fontSize: '15px', color: '#1a1a1a', fontWeight: '600'}}>Energy Cost Reduction</div>
                  <div style={{fontSize: '13px', color: '#555', marginTop: '5px'}}>Radisson: 30.2% verified (Dec 2025)</div>
                </div>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '44px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>1-2 Days</div>
                  <div style={{fontSize: '15px', color: '#1a1a1a', fontWeight: '600'}}>Installation Time</div>
                  <div style={{fontSize: '13px', color: '#555', marginTop: '5px'}}>Zero operational disruption</div>
                </div>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '44px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>No</div>
                  <div style={{fontSize: '15px', color: '#1a1a1a', fontWeight: '600'}}>Maintenance Required</div>
                  <div style={{fontSize: '13px', color: '#555', marginTop: '5px'}}>Set and forget technology</div>
                </div>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '44px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>10+ Years</div>
                  <div style={{fontSize: '15px', color: '#1a1a1a', fontWeight: '600'}}>Expected Lifespan</div>
                  <div style={{fontSize: '13px', color: '#555', marginTop: '5px'}}>Long-term performance</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div style={{textAlign: 'center', marginTop: '40px'}}>
              <Link 
                to="/oh4/calculator.html" 
                style={{
                  display: 'inline-block',
                  padding: '18px 45px',
                  background: '#ffa726',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  marginRight: '15px',
                  boxShadow: '0 4px 16px rgba(255,167,38,0.4)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Calculate Your Savings →
              </Link>
              <Link 
                to="/oh4" 
                style={{
                  display: 'inline-block',
                  padding: '18px 45px',
                  background: 'transparent',
                  color: '#00897b',
                  border: '2px solid #00897b',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  textDecoration: 'none'
                }}
              >
                Technical Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Solutions - Below Fold */}
      <section className="section bg-section" style={{padding: '50px 0'}}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2" style={{fontSize: '28px'}}>Complementary Energy Solutions</h2>
            <p className="section-subtitle" style={{fontSize: '16px'}}>
              Additional monitoring and compliance tools to support your energy management strategy
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', maxWidth: '950px', margin: '0 auto'}}>
            {services.slice(1).map((service, index) => (
              <Link to={service.link} key={index} style={{
                textDecoration: 'none',
                padding: '25px',
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s'
              }}>
                <h3 style={{fontSize: '18px', color: '#333', marginBottom: '10px', fontWeight: '600'}}>{service.title}</h3>
                <p style={{fontSize: '14px', color: '#666', marginBottom: '15px', lineHeight: '1.5'}}>{service.description}</p>
                <div style={{color: '#00897b', fontSize: '14px', fontWeight: '500'}}>
                  Learn more →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why Full-Bin UK</h2>
          </div>

          <div className="features-grid">
            <div className="feature-item">
              <h3 className="heading-3">UK-Focused Expertise</h3>
              <p className="body-medium">
                Built for UK businesses. We understand local regulations, climate conditions, and operational requirements.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="heading-3">Proven Technology</h3>
              <p className="body-medium">
                OH4 energy optimization and IoT sensors deployed across commercial, industrial, and hospitality sectors.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="heading-3">Real Results</h3>
              <p className="body-medium">
                Measurable outcomes: energy savings, cost reductions, and sustainability improvements you can track.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="heading-3">Simple Implementation</h3>
              <p className="body-medium">
                Non-invasive installations, cloud-based dashboards, and ongoing support. No disruption to operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Trusted by UK Businesses</h2>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <div className="author-name">{testimonial.author}</div>
                  <div className="author-company">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section - Multi-Path Conversion */}
      <section style={{
        background: 'linear-gradient(135deg, #004d40 0%, #00695c 100%)',
        padding: '80px 0',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container" style={{position: 'relative', zIndex: 2}}>
          {/* Main Headline */}
          <div style={{textAlign: 'center', maxWidth: '900px', margin: '0 auto 50px'}}>
            <h2 style={{fontSize: '48px', fontWeight: '700', marginBottom: '20px', lineHeight: '1.2'}}>
              Stop Overpaying for Energy
            </h2>
            <p style={{fontSize: '22px', marginBottom: '15px', opacity: '0.95', lineHeight: '1.5'}}>
              Join hotels, shopping centres, and industrial facilities across the UK, UAE, and Sweden who have already reduced their energy costs by up to 30% with OH4.
            </p>
            <p style={{fontSize: '18px', opacity: '0.85', marginTop: '15px'}}>
              <strong>Conservative estimate:</strong> A typical medium-sized facility can save £15,000-£45,000 annually.
            </p>
          </div>

          {/* Three-Path CTA Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto 50px'
          }}>
            {/* Path 1: Calculate Savings */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '35px 30px',
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              transition: 'transform 0.3s'
            }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: '#ffa726',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <TrendingDown size={36} color="white" />
              </div>
              <h3 style={{fontSize: '22px', fontWeight: '700', color: '#1a1a1a', marginBottom: '12px'}}>
                See Your Numbers
              </h3>
              <p style={{fontSize: '15px', color: '#555', marginBottom: '25px', lineHeight: '1.6'}}>
                Get a detailed ROI analysis based on your actual system specifications. Takes 2 minutes.
              </p>
              <Link
                to="/oh4/calculator.html"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  background: '#ffa726',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
              >
                Calculate Savings
              </Link>
            </div>

            {/* Path 2: Book Consultation */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '35px 30px',
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              transform: 'scale(1.05)',
              border: '3px solid #ffa726'
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#ffa726',
                color: 'white',
                padding: '6px 20px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '700',
                textTransform: 'uppercase'
              }}>
                Most Popular
              </div>
              <div style={{
                width: '70px',
                height: '70px',
                background: '#00897b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Zap size={36} color="white" />
              </div>
              <h3 style={{fontSize: '22px', fontWeight: '700', color: '#1a1a1a', marginBottom: '12px'}}>
                Free Energy Audit
              </h3>
              <p style={{fontSize: '15px', color: '#555', marginBottom: '25px', lineHeight: '1.6'}}>
                Our engineers will assess your facility and provide a custom implementation plan at no cost.
              </p>
              <Link
                to="/contact"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  background: '#00897b',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
              >
                Book Free Audit
              </Link>
            </div>

            {/* Path 3: Download Case Study */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '35px 30px',
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              transition: 'transform 0.3s'
            }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: '#455a64',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Award size={36} color="white" />
              </div>
              <h3 style={{fontSize: '22px', fontWeight: '700', color: '#1a1a1a', marginBottom: '12px'}}>
                Review the Evidence
              </h3>
              <p style={{fontSize: '15px', color: '#555', marginBottom: '25px', lineHeight: '1.6'}}>
                Download the full Radisson Palm Jumeirah pilot project report with verified data.
              </p>
              <a
                href="/downloads/radisson-case-study.html"
                target="_blank"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  background: '#455a64',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
              >
                Download Case Study
              </a>
            </div>
          </div>

          {/* Trust Indicators */}
          <div style={{
            textAlign: 'center',
            padding: '30px 20px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '40px', flexWrap: 'wrap'}}>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: '700'}}>500+</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>Installations</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: '700'}}>30.2%</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>Verified Savings</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: '700'}}>Zero</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>Maintenance</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '32px', fontWeight: '700'}}>10+ Years</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>Lifespan</div>
              </div>
            </div>
          </div>

          {/* Risk Reversal */}
          <div style={{textAlign: 'center', marginTop: '40px', fontSize: '16px', opacity: '0.9'}}>
            <p>
              <Shield size={20} style={{verticalAlign: 'middle', marginRight: '8px'}} />
              <strong>No Risk, No Obligation.</strong> Start with a free energy audit or ROI calculation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
